/*
  Custom Badge System (Promo Badge) - JavaScript Version
  Duplicates the functionality of snippets/custom-badge.liquid
  
  Usage:
    const promoBadgeSystem = new CustomBadgeSystem();
    const badgeHTML = promoBadgeSystem.renderPromoBadge(product, options);
*/

class CustomBadgeSystem {
  constructor() {
    this.settings = window.theme?.promoBadge || {};
  }

  /*
    Convert hex color to RGB components
  */
  hexToRgb(hex) {
    if (!hex) return { r: 0, g: 0, b: 0 };

    hex = hex.replace("#", "");

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    return { r, g, b };
  }

  /*
    Check if badge should be hidden based on conditions
  */
  shouldHideBadge(product, options = {}) {
    if (options.excludedTitles && Array.isArray(options.excludedTitles)) {
      if (options.excludedTitles.includes(product.title)) {
        return true;
      }
    }

    if (options.cardCollection?.handle === "steals-deals" || options.collection?.handle === "steals-deals") {
      return true;
    }

    return false;
  }

  /*
    Round savings percentage to nearest multiple of 5
  */
  roundToNearestFive(percentage) {
    const remainder = percentage % 5;
    if (remainder > 2) {
      return percentage + 5 - remainder;
    } else {
      return percentage - remainder;
    }
  }

  /*
    Calculate savings percentage
  */
  calculateSavingsPercentage(compareAtPrice, price) {
    if (!compareAtPrice || compareAtPrice <= price) {
      return 0;
    }

    const savingsPercentage = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);

    if (this.settings.displayType === "percentage") {
      return this.roundToNearestFive(savingsPercentage);
    }

    return savingsPercentage;
  }

  /*
    Calculate promo badge exactly like Liquid does
  */
  calculatePromoBadge(product, options = {}) {
    if (!this.settings.enabled) {
      return {
        shouldShow: false,
        badgeHTML: null,
        savingsPercentage: 0,
      };
    }

    if (this.shouldHideBadge(product, options)) {
      return {
        shouldShow: false,
        badgeHTML: null,
        savingsPercentage: 0,
      };
    }

    const variant = (product.variants && product.variants[0]) || {};

    if (!variant.compare_at_price || variant.compare_at_price <= variant.price) {
      return {
        shouldShow: false,
        badgeHTML: null,
        savingsPercentage: 0,
      };
    }

    const savingsPercentage = this.calculateSavingsPercentage(variant.compare_at_price, variant.price);

    const opacity = (this.settings.opacity || 100) / 100.0;
    const padding = this.settings.padding || 3;
    const width = this.settings.width || 50;
    const height = this.settings.height || 20;
    const fontSize = this.settings.fontSize || 10;
    const bgColor = this.settings.bgColor || "#ff0000";
    const textColor = this.settings.textColor || "#ffffff";
    const borderRadius = this.settings.borderRadius || 0;

    const paddingMobile = this.settings.paddingMobile || padding;
    const widthMobile = this.settings.widthMobile || width;
    const heightMobile = this.settings.heightMobile || height;
    const fontSizeMobile = this.settings.fontSizeMobile || fontSize;

    const rgb = this.hexToRgb(bgColor);
    const rgbaBackground = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;

    const badgeHTML = `
      <div class="promo-badge-wrapper position-absolute" style="z-index: 2; top: 10px; left: 10px;">
        <span
          class="promo-badge badge"
          style="
            padding: ${padding}px;
            width: ${width}px;
            height: ${height}px;
            font-size: ${fontSize}px;
            background-color: ${rgbaBackground};
            color: ${textColor};
            border-radius: ${borderRadius}px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-weight: 500;
            text-transform: uppercase;
            line-height: 1.2;
          "
          data-mobile-padding="${paddingMobile}"
          data-mobile-width="${widthMobile}"
          data-mobile-height="${heightMobile}"
          data-mobile-font-size="${fontSizeMobile}"
          data-rgba-bg="${rgbaBackground}"
        >
          ${savingsPercentage}% OFF
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML: badgeHTML,
      savingsPercentage: savingsPercentage,
      settings: {
        opacity,
        padding,
        width,
        height,
        fontSize,
        bgColor,
        textColor,
        borderRadius,
        paddingMobile,
        widthMobile,
        heightMobile,
        fontSizeMobile,
        rgbaBackground,
      },
    };
  }

  /*
    Render promo badge (main public method)
  */
  renderPromoBadge(product, options = {}) {
    const badgeData = this.calculatePromoBadge(product, options);

    if (badgeData.shouldShow) {
      return badgeData.badgeHTML;
    }

    return "";
  }

  /*
    Get CSS styles for promo badges (for responsive design)
  */
  getPromoBadgeCSS() {
    return `
      <style>
        .promo-badge-wrapper {
          z-index: 2;
        }

        .promo-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 500;
          text-transform: uppercase;
          line-height: 1.2;
        }

        @media screen and (max-width: 767px) {
          .promo-badge-wrapper {
            top: 5px !important;
            left: 5px !important;
          }

          .promo-badge {
            padding: var(--mobile-padding, 3px) !important;
            width: var(--mobile-width, 50px) !important;
            height: var(--mobile-height, 20px) !important;
            font-size: var(--mobile-font-size, 10px) !important;
            background-color: var(--rgba-bg) !important;
          }
        }

        @media screen and (min-width: 768px) {
          .promo-badge-wrapper {
            top: 10px !important;
            left: 10px !important;
          }
        }
      </style>
    `;
  }

  /*
    Apply responsive styles dynamically (since CSS custom properties in style attributes are limited)
  */
  applyResponsiveStyles() {
    const promoBadges = document.querySelectorAll(".promo-badge");

    promoBadges.forEach((badge) => {
      const wrapper = badge.closest(".promo-badge-wrapper");
      if (!wrapper) return;

      const mobilePadding = badge.dataset.mobilePadding;
      const mobileWidth = badge.dataset.mobileWidth;
      const mobileHeight = badge.dataset.mobileHeight;
      const mobileFontSize = badge.dataset.mobileFontSize;
      const rgbaBg = badge.dataset.rgbaBg;

      const applyMobileStyles = () => {
        if (window.innerWidth <= 767) {
          wrapper.style.top = "5px";
          wrapper.style.left = "5px";
          badge.style.padding = `${mobilePadding}px`;
          badge.style.width = `${mobileWidth}px`;
          badge.style.height = `${mobileHeight}px`;
          badge.style.fontSize = `${mobileFontSize}px`;
          badge.style.backgroundColor = rgbaBg;
        } else {
          wrapper.style.top = "10px";
          wrapper.style.left = "10px";
        }
      };

      applyMobileStyles();
      window.addEventListener("resize", applyMobileStyles);
    });
  }
}

if (typeof window !== "undefined") {
  window.CustomBadgeSystem = CustomBadgeSystem;
}
